import { useCallback, useEffect, useMemo, useState } from "react";
import { IoMdCheckmark } from "react-icons/io";
import { IoChevronDownOutline, IoChevronUpOutline } from "react-icons/io5";
import { useDispatch } from "react-redux";
import { Transition, useSpring } from "react-spring";
import Backdrop from "../../../../../components/backdrop/backdrop";
import { Candidate } from "../../../../../models/candidate.model";
import { updateRecruiterResponsible } from "../../../../../store/event/event-actions";
import { getCroppedString } from "../../../../../utils/string.utils";
import {
  DropdownItem,
  DropdownMenu,
  DropdownSpan,
  FilterButton,
  FilterContainer,
} from "../filter/index.styled";
import { FilterLabel } from "./index.styled";

const NAME_SIZE = 15;

export interface SelectOption {
  value: string;
  label: string;
}

interface RecruiterSelectProps {
  options: SelectOption[];
  label?: string;
  initial?: SelectOption;
  candidate: Candidate;
  actionId?: string;
}

const RecruiterSelect: React.FC<RecruiterSelectProps> = ({
  options,
  label,
  initial,
  candidate,
  actionId,
}) => {
  const [dropdown, setDropdown] = useState<boolean>(false);
  const dispatch = useDispatch();
  const [current, setCurrent] = useState<SelectOption>(
    initial || {
      label: candidate?.last_action?.recruiter.name || "Selecione",
      value: candidate?.last_action?.recruiter.id || "1",
    }
  );
  const styledMenu = useSpring({
    transform: dropdown ? "translate3D(0,25px,0)" : "translate3D(0,-40px,0)",
    opacity: dropdown ? 1 : 0,
  });

  useEffect(() => {
    if (candidate?.last_action?.recruiter) {
      setCurrent({
        label: candidate?.last_action?.recruiter.name,
        value: candidate?.last_action?.recruiter.id,
      });
    }
  }, [candidate]);

  useEffect(() => {
    if (initial && current && initial.value !== current.value) {
      setCurrent(initial);
    }
  }, [current, initial]);

  const handleClick = useCallback(
    (item: SelectOption) => {
      setCurrent(item);
      setDropdown(false);
      if (actionId) {
        dispatch(updateRecruiterResponsible(actionId, item.value));
      }
    },
    [dispatch, actionId]
  );

  const filterLabel = useMemo(
    () => getCroppedString(current.label, NAME_SIZE),
    [current.label]
  );

  return (
    <FilterContainer>
      <FilterButton
        onClick={() => options.length > 1 && setDropdown((prev) => !prev)}
      >
        <FilterLabel>{filterLabel}</FilterLabel>
        {options.length > 1 &&
          (dropdown ? <IoChevronUpOutline /> : <IoChevronDownOutline />)}
      </FilterButton>
      {dropdown && (
        <Backdrop
          toggle={() => setDropdown(false)}
          isShown={dropdown}
          zIndex={100}
          transparent
        />
      )}
      <Transition items={dropdown}>
        {(styles, item) =>
          item && (
            <DropdownMenu style={styledMenu} isShown={dropdown}>
              {options
                .filter((o) => o.value !== current.value)
                .map((item) => (
                  <DropdownItem
                    key={item.value}
                    onClick={() => {
                      handleClick(item);
                    }}
                    isActive={item.value === current.value}
                  >
                    <DropdownSpan>{item.label}</DropdownSpan>
                    <IoMdCheckmark />
                  </DropdownItem>
                ))}
            </DropdownMenu>
          )
        }
      </Transition>
    </FilterContainer>
  );
};

export default RecruiterSelect;
